import React, { useEffect } from 'react';
import { useRouter } from '@framework/router';
import cn from 'classnames';
import isBoolean from 'lodash/isBoolean';
import IconClose from '@creditflowresearch/uilib/Icon/IconClose';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import PortalWrp, { PortalId } from '@framework/ui/Templates/PortalWrp';
import type { ITimeZone } from '@dealroadshow/uikit-date-time';
import { toApiTimestamp } from '@/creditflowresearch/application/helpers/zonedDayBounds';
import { useUserTimeZoneContext } from '@/creditflowresearch/application/User/UserTimeZone/UserTimeZoneContext';
import { IItemRange } from '@/creditflowresearch/domain/vo/Bondscreener/IItemsFilter';

import hInteraction from '@creditflowresearch/uilib/styles/helpers/interaction.module.scss';
import headerStyles from '@creditflowresearch/uilib/styles/headers.module.scss';
import styles from '@framework/ui/Organisms/Filters/FiltersPanel/sidebar.module.scss';

interface IProps {
  FooterComponent: React.ElementType,
  DatePeriodComponent: React.ElementType,
  SectionsComponent: React.ElementType,
  TagsComponent: React.ElementType,
  isOpen?: boolean,
  dateFromMin: number,
  isFetching: boolean,
  appliedFilters: Object,
  onSidebarToggle?: Function,
  isFilterChecked?: Function,
  handleFilterUpdate?: Function,
  handleParentFilterChange?: Function,
  toggleParentFilter?: Function,
  handleFilterByChange?: Function,
  onFilterApply: Function,
  resetFilters: Function,
  dateBoundsZone?: ITimeZone,
}

interface IFilterValue {
  selectedRangeValue: IItemRange,
  minValue: number | null,
  maxValue: number | null,
}

const isFilterValue = (obj): obj is IFilterValue => {
  return obj && typeof obj === 'object' && 'minValue' in obj && 'maxValue' in obj && 'range' in obj;
};

const Sidebar = (props: IProps) => {
  const {
    FooterComponent,
    DatePeriodComponent,
    SectionsComponent,
    TagsComponent,
    isOpen,
  } = props;
  const router = useRouter();
  const { timeZone } = useUserTimeZoneContext();
  const dateBoundsZone = props.dateBoundsZone ?? timeZone;

  const sidebarCls = cn(styles.sidebar, styles.responsiveContainer, {
    [styles.isSidebarOpen]: isOpen,
  });

  const sidebarOverlayCls = cn(styles.sidebarOverlay, {
    [styles.isSidebarOpen]: isOpen,
  });

  // State keeps the inclusive day picked, so the picker always shows it; any exclusive-bound
  // adjustment an RPC needs happens where that payload is built.
  const handleStartDateChange = (dateFrom: number | Date) => {
    if (dateFrom) {
      props.handleFilterUpdate({
        dateFrom: toApiTimestamp(new Date(dateFrom), 'start', dateBoundsZone),
      });
    }
  };

  const handleEndDateChange = (dateTo: Date | number) => {
    if (dateTo) {
      props.handleFilterUpdate({
        dateTo: toApiTimestamp(new Date(dateTo), 'end', dateBoundsZone),
      });
    }
  };

  const toggleSidebar = () => {
    if (props.onSidebarToggle) {
      props.onSidebarToggle();
    }
  };

  const handleSidebarClose = () => {
    // Reset filters to applied state
    props.handleFilterUpdate(props.appliedFilters);
    toggleSidebar();
  };

  const handleFilterApply = () => {
    props.onFilterApply(router);
    toggleSidebar();
  };

  const handleFilterReset = () => {
    props.resetFilters(router);
  };

  const handleTagRemove = (filterName: string, filterValue: IFilterValue | Boolean) => {
    const isRange = isFilterValue(filterValue);

    if (filterName === 'dates') {
      return props.handleFilterUpdate({
        dateFrom: props.dateFromMin,
        dateTo: null,
      });
    }

    if (isRange) {
      return props.handleFilterUpdate({
        [filterName]: {
          ...filterValue,
          range: {
            min: filterValue.minValue,
            max: filterValue.maxValue,
          },
        },
      });
    }

    if (filterName === 'includedStatus') {
      return props.handleFilterUpdate({
        [filterName]: 'all',
      });
    }

    if (filterName === 'includedRegionIds') {
      return props.handleFilterUpdate({
        includedRegionIds: [],
        includedCountryIds: [],
      });
    }

    if (filterName === 'includedSectorIds') {
      return props.handleFilterUpdate({
        includedSectorIds: [],
        includedSubsectorIds: [],
      });
    }

    if (isBoolean(filterValue)) {
      return props.handleFilterUpdate({
        [filterName]: !filterValue,
      });
    }

    return props.handleFilterByChange(filterName, filterValue);
  };

  useEffect(() => {
    if (props.isOpen) {
      document.body.classList.add(hInteraction.nonScrollable);
    } else {
      document.body.classList.remove(hInteraction.nonScrollable);
    }

    return () => {
      document.body.classList.remove(hInteraction.nonScrollable);
    };
  }, [props.isOpen]);

  return (
    <PortalWrp portalId={ PortalId.PORTAL_OVERLAY_ID }>
      <>
        <div
          className={ sidebarOverlayCls }
          onClick={ handleSidebarClose }
        />
        <div className={ sidebarCls } data-test="sidebarFilters">
          <div className={ styles.sidebarHeader }>
            <div className={ styles.sidebarHeaderContent }>
              <div className={ styles.headerTitle }>
                <div className={ headerStyles.isH2 }>Filters</div>
              </div>
              <DatePeriodComponent
                timeZone={ dateBoundsZone.timeZone }
                onDateFromChange={ handleStartDateChange }
                onDateToChange={ handleEndDateChange }
              />
              <div className={ styles.headerClose } data-test="closeFiltersButton">
                <span onClick={ handleSidebarClose }>
                  <IconClose />
                </span>
              </div>
            </div>
          </div>
          <div className={ styles.sidebarWrp }>
            <Spinner
              overlay
              overlayClassName={ styles.spinnerOverlay }
              isVisible={ props.isFetching }
            />
            <SectionsComponent
              isFilterChecked={ props.isFilterChecked }
              handleParentFilterChange={ props.handleParentFilterChange }
              handleFilterByChange={ props.handleFilterByChange }
              handleFilterUpdate={ props.handleFilterUpdate }
              toggleParentFilter={ props.toggleParentFilter }
              isOpen={ props.isOpen }
            />
          </div>
          <FooterComponent
            onApply={ handleFilterApply }
            onReset={ handleFilterReset }
            tagsComponent={ TagsComponent }
            onTagRemove={ handleTagRemove }
            className={ styles.footerContent }
          />
        </div>
      </>
    </PortalWrp>
  );
};

export default Sidebar;
